import axios from 'axios';
import { MediaFile, MediaListResponse } from '../types/admin.types';
import { authErrorEvent } from './api/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Media Service
 * Handles file upload, download, and media management operations
 */
class MediaService {
  /**
   * Get authorization headers with access token
   */
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Upload file to server
   */
  async uploadFile(file: File, isPublic: boolean = false, tags?: string[], description?: string, categoryId?: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('isPublic', isPublic.toString());
      if (tags && tags.length > 0) {
        formData.append('tags', tags.join(','));
      }
      if (description) {
        formData.append('description', description);
      }
      if (categoryId) {
        formData.append('categoryId', categoryId);
      }

      const response = await axios.post(`${API_BASE_URL}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...this.getAuthHeaders(),
        },
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get media list with pagination
   * @param page - Page number (1-indexed)
   * @param limit - Items per page
   * @param isPublic - Optional filter for public/private media. If undefined, returns all media
   * @param categoryId - Optional filter by category ID
   */
  async listMedia(page: number = 1, limit: number = 10, isPublic?: boolean, categoryId?: string): Promise<MediaListResponse> {
    try {
      const params: any = { page, limit };
      if (isPublic !== undefined) {
        params.isPublic = isPublic;
      }
      if (categoryId) {
        params.categoryId = categoryId;
      }
      
      const response = await axios.get(`${API_BASE_URL}/media`, {
        params,
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get media by ID
   */
  async getMediaById(id: string): Promise<MediaFile> {
    try {
      const response = await axios.get(`${API_BASE_URL}/media/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get public media
   */
  async getPublicMedia(limit: number = 50) {
    try {
      const response = await axios.get(`${API_BASE_URL}/media/public`, {
        params: { limit },
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update media metadata
   */
  async updateMedia(id: string, data: Partial<MediaFile>): Promise<MediaFile> {
    try {
      const payload: any = {};
      
      if (data.description !== undefined) payload.description = data.description;
      if (data.tags !== undefined) payload.tags = data.tags?.join(',');
      if (data.isPublic !== undefined) payload.isPublic = data.isPublic;

      const response = await axios.patch(`${API_BASE_URL}/media/${id}`, payload, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete media file
   */
  async deleteMedia(id: string): Promise<{ id: string }> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/media/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get file download URL
   */
  getDownloadUrl(filepath: string): string {
    return `${API_BASE_URL}/media/download/${filepath}`;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get file type icon/category
   */
  getFileCategory(mimeType: string): 'image' | 'video' | 'document' | 'audio' | 'other' {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (
      mimeType.includes('pdf') ||
      mimeType.includes('word') ||
      mimeType.includes('excel') ||
      mimeType.includes('powerpoint')
    ) {
      return 'document';
    }
    return 'other';
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;
      const message = error.response?.data?.message || error.message;

      // Handle authentication errors (401/403)
      if (statusCode === 401 || statusCode === 403) {
        // Clear stored tokens
        if (typeof window !== 'undefined') {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }

        // Dispatch custom event to notify app
        const event = new CustomEvent('authError', {
          detail: {
            statusCode,
            message: statusCode === 401 ? 'Token expired or invalid' : 'Access forbidden',
            timestamp: new Date().toISOString(),
          },
        });
        authErrorEvent.dispatchEvent(event);

        console.warn(
          `%c🔐 Authentication Error [${statusCode}]`,
          'color: #ff6600; font-weight: bold;',
          'Redirecting to login...'
        );
      }

      return new Error(message);
    }
    return error;
  }
}

const mediaService = new MediaService();
export default mediaService;
